/**
 * Password reset (`/reset-password?token=…`) — completes the admin-issued reset
 * of docs/05-api-spec.md §11.4, v1's no-SMTP recovery path (§3.2).
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PASSWORD_MIN_LENGTH } from '@famtree/shared';
import { authApi } from '../../api/client.js';
import { sessionQueryKey } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import styles from '../../ui/components.module.css';

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword(token, password),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
      navigate('/', { replace: true });
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    reset.mutate();
  };

  return (
    <main className={styles.centeredPage}>
      <div className={styles.centeredPanel}>
        <div className={styles.brand}>
          <h1 className={styles.brandName}>{t('reset.title')}</h1>
        </div>

        <form className={styles.card} onSubmit={onSubmit} noValidate>
          {!token && (
            <p className={styles.problem} role="alert">
              {t('reset.missingToken')}
            </p>
          )}
          {reset.isError && (
            <p className={styles.problem} role="alert">
              {problemMessage(reset.error, t)}
            </p>
          )}
          {mismatch && (
            <p className={styles.problem} role="alert">
              {t('invite.mismatch')}
            </p>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              {t('reset.password')}
            </label>
            <input
              id="password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirm">
              {t('reset.confirm')}
            </label>
            <input
              id="confirm"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <button className={styles.button} type="submit" disabled={!token || reset.isPending}>
            {t('reset.submit')}
          </button>
        </form>
      </div>
    </main>
  );
}
